/**
 * FormatToolbar: the small formatting bar above the manuscript.
 * active is a list of ids currently on (aria-pressed). Arrow keys move between buttons (roving tabindex).
 * (cockatrice-ui `src/ui/FormatToolbar.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/format-toolbar.css';
import { createSignal, mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, each, el } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface FormatTool {
  /** Id passed to onAction and matched against active. */
  readonly id: string;
  /** Accessible name and tooltip. */
  readonly label: string;
  /** Text glyph, e.g. "B". */
  readonly glyph?: string | undefined;
  /** Icon name instead of a glyph. */
  readonly icon?: IconName | undefined;
  /** Keyboard shortcut, e.g. "Ctrl+B". */
  readonly shortcut?: string | undefined;
  /** false for one-shot actions (no aria-pressed). */
  readonly toggle?: boolean | undefined;
  /**
   * Shown but not usable now (a tool still to come, or nothing to act on): aria-disabled, never pressed, its press
   * ignored. It keeps its place in the arrow keys' order, so it is found and its name heard. May be a getter.
   */
  readonly disabled?: boolean | undefined;
  /** Extra classes on the tool's button. */
  readonly class?: string | undefined;
}

export interface FormatToolbarProps extends Attributes {
  /** Tools. Default Bold, Italic, Small caps, Quote, Insert picture. */
  readonly tools?: readonly FormatTool[] | undefined;
  /** Ids currently on at the caret. */
  readonly active?: readonly string[] | undefined;
  /** A tool was pressed; next is the new pressed state. */
  readonly onAction?: ((id: string, next: boolean) => void) | undefined;
  /** Toolbar name. Default "Formatting". */
  readonly label?: string | undefined;
  /** Disable every tool (read-only access). */
  readonly disabled?: boolean | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

type ToolKey = 'ui.format-toolbar.bold' | 'ui.format-toolbar.italic' | 'ui.format-toolbar.small-caps' | 'ui.format-toolbar.quote' | 'ui.format-toolbar.picture';
/** A tool whose label is the catalog's, read where it shows. */
const tool = (key: ToolKey, rest: Omit<FormatTool, 'label'>): FormatTool => ({
  ...rest,
  get label() {
    return uiText()(key);
  },
});

export const defaultFormatTools: readonly FormatTool[] = [
  tool('ui.format-toolbar.bold', { id: 'bold', glyph: 'B', shortcut: 'Ctrl+B' }),
  tool('ui.format-toolbar.italic', { id: 'italic', glyph: 'I', shortcut: 'Ctrl+I' }),
  tool('ui.format-toolbar.small-caps', { id: 'smallcaps', glyph: 'Sc' }),
  tool('ui.format-toolbar.quote', { id: 'quote', glyph: '“”' }),
  tool('ui.format-toolbar.picture', { id: 'picture', icon: 'picture', toggle: false }),
];

export default function FormatToolbar(props: FormatToolbarProps): Child {
  const [local, rest] = splitProps(props, ['tools', 'active', 'onAction', 'label', 'disabled', 'class']);
  const tools = (): readonly FormatTool[] => local.tools ?? defaultFormatTools;
  const [focus, setFocus] = createSignal(0);
  let root: HTMLDivElement | undefined;
  const move = (e: KeyboardEvent): void => {
    const n = tools().length;
    let i = focus();
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') i = (i + 1) % n;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') i = (i - 1 + n) % n;
    else if (e.key === 'Home') i = 0;
    else if (e.key === 'End') i = n - 1;
    else return;
    e.preventDefault();
    setFocus(i);
    root?.querySelectorAll('button')[i]?.focus();
  };
  const on = (id: string): boolean => (local.active ?? []).includes(id);
  return el(
    'div',
    mergeProps({
      ref: (node: HTMLDivElement) => {
        root = node;
      },
      class: () => `ui-format-toolbar${local.class !== undefined && local.class !== '' ? ` ${local.class}` : ''}`,
      role: 'toolbar',
      'aria-label': () => (local.label !== undefined && local.label !== '' ? local.label : uiText()('ui.format-toolbar.label')),
      onKeyDown: move,
    }, rest),
    each(tools, (t, i) =>
      el(
        'button',
        {
          type: 'button',
          class: `ui-format-toolbar__btn ui-format-toolbar__btn--${t.id}${t.class !== undefined && t.class !== '' ? ` ${t.class}` : ''}`,
          tabindex: () => (focus() === i() ? 0 : -1),
          'aria-pressed': () => (t.toggle === false || t.disabled === true ? undefined : on(t.id) ? 'true' : 'false'),
          'aria-disabled': () => (t.disabled === true ? 'true' : undefined),
          'aria-label': () => t.label,
          'aria-keyshortcuts': () => (t.shortcut === undefined || t.shortcut === '' ? undefined : t.shortcut.replace('Ctrl', 'Control')),
          title: () => (t.shortcut === undefined || t.shortcut === '' ? t.label : uiText()('ui.format-toolbar.title', { label: t.label, shortcut: t.shortcut })),
          disabled: () => local.disabled,
          onFocus: () => setFocus(i()),
          onMouseDown: (e: MouseEvent) => {
            e.preventDefault(); // keep the text selection
          },
          onClick: () => {
            if (t.disabled !== true) local.onAction?.(t.id, !on(t.id));
          },
        },
        t.icon !== undefined ? component(Icon, { name: t.icon }) : el('span', { 'aria-hidden': 'true' }, t.glyph),
      ),
    ),
  );
}
